import { useEffect, useState } from "react";
import { Button, Grid, Stack, Tooltip, Typography } from "@mui/material";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormValues, NodeInformation } from "../../..";
import { FilterIcon, SchemaIcon } from "../../../../../../../assets/icons";
import FormSelect from "../../../../../../../components/forms/FormSelect";
import FormTextField from "../../../../../../../components/forms/FormTextField";
import {
	FilterDTO,
	FiltersListDTO,
	SiteGoodsFlowDTO,
	SiteGoodsFlowItemDTO,
} from "../../../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../../../datasources/networking/apiRoutes";
import { Option } from "../../../../../../../datasources/responses/common";
import { useReadResource } from "../../../../../../../hooks/useResource";
import { usePermissions } from "../../../../../../../permissions/usePermissions";
import { FiltersDialog } from "./components/FiltersDialog";
import { GoodsFlowDialog } from "./components/GoodsFlowDialog";

type GeneralInformationProps = {
	form: UseFormReturn<FormValues>;
	valueAddedServices: Option[];
	computeFlags: Option[];
	nodeActivities: Option[];
	certifications: Option[];
	sectors: Option[];
	loading: boolean;
	isNewOperationalUnit: boolean;
	nodeInformation: NodeInformation;
	mode: "full" | "limited";
};

export const GeneralInformation = ({
	form,
	valueAddedServices,
	computeFlags,
	nodeActivities,
	loading,
	isNewOperationalUnit,
	certifications,
	sectors,
	nodeInformation,
	mode,
}: GeneralInformationProps) => {
	const { t } = useTranslation(["masterData", "common"]);

	const [openGoodFlowDialog, setOpenGoodFlowDialog] = useState(false);
	const [openFiltersDialog, setOpenFiltersDialog] = useState(false);

	const [itemsGoodsFlow, setItemsGoodsFlow] = useState<SiteGoodsFlowItemDTO[]>([]);
	const [itemsFilters, setItemsFilters] = useState<FilterDTO[][]>([]);

	// filters and goods flow can be managed only if they can be read
	const { canCall } = usePermissions();
	const isFiltersForbidden = !canCall(ApiRoutes.ConfigurationFilters, "GET");
	const isGoodsFlowForbidden = !canCall(ApiRoutes.ConfigurationGoodFlows, "GET");

	const goodsFlows = useReadResource<undefined, SiteGoodsFlowDTO>(
		{ route: ApiRoutes.ConfigurationGoodFlows, path: { id: nodeInformation.id } },
		{ disabled: isNewOperationalUnit }
	);

	const filters = useReadResource<undefined, FiltersListDTO>(
		{ route: ApiRoutes.ConfigurationFilters, path: { id: nodeInformation.id } },
		{ disabled: isNewOperationalUnit }
	);

	useEffect(() => {
		if (filters.data?.items) {
			setItemsFilters(filters.data.items);
		}
	}, [goodsFlows.data, filters.data]);

	useEffect(() => {
		if (goodsFlows.data?.items) {
			setItemsGoodsFlow(goodsFlows.data.items);
		}
	}, [goodsFlows.data]);

	const handleOpenFiltersDialog = () => {
		setOpenFiltersDialog(true);
	};

	const handleCloseFiltersDialog = () => {
		setOpenFiltersDialog(false);
	};

	const handleOpenGoodFlowDialog = () => {
		setOpenGoodFlowDialog(true);
	};

	const handleCloseGoodFlowDialog = () => {
		setOpenGoodFlowDialog(false);
	};

	const dialogContent = {
		title: t("detail/inclusion of node emission in transport calculation"),
		content: (
			<>
				<Typography variant="body2">
					{t("detail/inclusion of node emission in transport calculation dialog/paragraph")}
				</Typography>
				<Typography variant="body2" fontWeight={700} pt={1}>
					{t("detail/inclusion of node emission in transport calculation dialog/subTitle1")}
				</Typography>
				<Typography variant="body2">
					{t("detail/inclusion of node emission in transport calculation dialog/text1")}
				</Typography>
				<Typography variant="body2" fontWeight={700} pt={1}>
					{t("detail/inclusion of node emission in transport calculation dialog/subTitle2")}
				</Typography>
				<Typography variant="body2">
					{t("detail/inclusion of node emission in transport calculation dialog/text2")}
				</Typography>
				<Typography variant="body2" fontWeight={700} pt={1}>
					{t("detail/inclusion of node emission in transport calculation dialog/subTitle3")}
				</Typography>
				<Typography variant="body2">
					{t("detail/inclusion of node emission in transport calculation dialog/text3")}
				</Typography>
			</>
		),
	};

	const isReadOnly = form.watch("locked");

	return (
		<>
			<Grid container spacing={2}>
				<Grid size={6}>
					<FormTextField
						name="area"
						label={t("detail/covered area")}
						form={form}
						fullWidth
						measure="m²"
						isTypeNumber={true}
						readOnly={isReadOnly}
						isLoading={loading}
						onlyPositiveNumbers
					/>
				</Grid>

				{mode === "full" && (
					<Grid size={6}>
						<FormTextField
							name="capacity"
							label={t("detail/capacity")}
							form={form}
							fullWidth
							measure={t("detail/pallet spaces")}
							isTypeNumber={true}
							readOnly={isReadOnly}
							isLoading={loading}
							onlyPositiveNumbers
						/>
					</Grid>
				)}

				<Grid size={6}>
					<FormTextField
						name="temperature"
						label={t("detail/temperature")}
						form={form}
						fullWidth
						measure="°C"
						isTypeNumber={true}
						readOnly={isReadOnly}
						isLoading={loading}
					/>
				</Grid>

				{mode === "full" && (
					<>
						<Grid size={6}>
							<FormTextField
								name="constructionYear"
								label={t("detail/construction year")}
								form={form}
								fullWidth
								isTypeNumber={true}
								readOnly={isReadOnly}
								isLoading={loading}
								onlyPositiveNumbers
							/>
						</Grid>
						<Grid size={6}>
							<FormSelect
								name="valueAddedServiceId"
								label={t("detail/value added services")}
								form={form}
								options={[{ id: "", label: t("common:none") }, ...valueAddedServices]}
								readOnly={isReadOnly}
								isLoading={loading}
							/>
						</Grid>
						<Grid size={6}>
							<FormSelect
								name="activityId"
								label={t("detail/site activities")}
								form={form}
								options={[{ id: "", label: t("common:none") }, ...nodeActivities]}
								readOnly={isReadOnly}
								isLoading={loading}
							/>
						</Grid>
						<Grid size={6}>
							<FormSelect
								name="buildingCertificationId"
								label={t("detail/certification")}
								form={form}
								options={[{ id: "", label: "none" }, ...certifications]}
								readOnly={isReadOnly}
								isLoading={loading}
							/>
						</Grid>
						<Grid size={6}>
							<FormSelect
								name="sector"
								label={t("detail/sector")}
								form={form}
								options={[{ id: "", label: "none" }, ...sectors]}
								readOnly={isReadOnly}
								isLoading={loading}
							/>
						</Grid>
						<Grid size={12}>
							<FormSelect
								name="computeFlag"
								label={t("detail/inclusion of node emission in transport calculation")}
								form={form}
								dialog={dialogContent}
								options={computeFlags}
								readOnly={isReadOnly}
								isLoading={loading}
								rules={{ required: t("detail/field required") }}
							/>
						</Grid>
					</>
				)}

				<Grid size={mode === "limited" ? 12 : 6}>
					<Tooltip
						placement="top"
						title={
							isFiltersForbidden ? (
								t("common:content locked/content forbidden")
							) : isReadOnly || isNewOperationalUnit ? (
								<Stack>
									<Typography variant="caption" fontWeight={700}>
										{t("detail/manage filters")}
									</Typography>
									<Typography variant="caption" lineHeight={1.25}>
										{t("detail/available once the operational unit is created")}
									</Typography>
								</Stack>
							) : (
								""
							)
						}
					>
						<span>
							<Button
								variant="outlined"
								onClick={handleOpenFiltersDialog}
								fullWidth
								disabled={isReadOnly || isNewOperationalUnit || loading || isFiltersForbidden}
								startIcon={<FilterIcon />}
								sx={{
									lineHeight: 1.05,
								}}
							>
								{t("detail/manage filters")} ({itemsFilters.length})
							</Button>
						</span>
					</Tooltip>
				</Grid>

				{mode === "full" && (
					<Grid size={6}>
						<Tooltip
							placement="top"
							title={
								isGoodsFlowForbidden ? (
									t("common:content locked/content forbidden")
								) : isReadOnly || isNewOperationalUnit ? (
									<Stack>
										<Typography variant="caption" fontWeight={700}>
											{t("detail/manage goods flow")}
										</Typography>
										<Typography variant="caption" lineHeight={1.25}>
											{t("detail/available once the operational unit is created")}
										</Typography>
									</Stack>
								) : (
									""
								)
							}
						>
							<span>
								<Button
									variant="outlined"
									onClick={handleOpenGoodFlowDialog}
									fullWidth
									disabled={isReadOnly || isNewOperationalUnit || loading || isGoodsFlowForbidden}
									startIcon={<SchemaIcon />}
								>
									{t("detail/manage goods flow")} ({itemsGoodsFlow.length})
								</Button>
							</span>
						</Tooltip>
					</Grid>
				)}
			</Grid>

			<FiltersDialog
				open={openFiltersDialog}
				handleClose={handleCloseFiltersDialog}
				nodeID={nodeInformation.id}
				items={itemsFilters}
				onChangeItems={setItemsFilters}
			/>

			{mode === "full" && (
				<GoodsFlowDialog
					open={openGoodFlowDialog}
					handleClose={handleCloseGoodFlowDialog}
					nodeInformation={nodeInformation}
					items={itemsGoodsFlow}
					onChangeItems={setItemsGoodsFlow}
				/>
			)}
		</>
	);
};
